import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api.js";
import { ErrorState, Loading, PageHeader } from "../components/Layout.js";
import { useAsync } from "../hooks.js";
import type { BootstrapData, Workout } from "../types.js";

export function DashboardPage() {
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useAsync(() => api.get<BootstrapData>("/api/bootstrap"), []);
  const [duration, setDuration] = useState<60 | 90>(60);
  const [location, setLocation] = useState<"gym" | "home">("gym");
  const [generating, setGenerating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorState message={error ?? "No data"} retry={refresh} />;

  const workout = data.suggestion;
  const start = async (selected: Workout) => {
    const started = await api.post<Workout>(`/api/workouts/${selected.id}/start`);
    navigate(`/workout/${started.id}`);
  };
  const generate = async () => {
    if (!window.confirm("Generate today’s workout with the OpenAI API? This uses separately billed API credits.")) return;
    setGenerating(true); setActionError(null);
    try {
      const generated = await api.post<Workout>("/api/ai/generate", { location, durationPresetMinutes: duration, energy: 3, soreness: [], constraints: "" });
      await refresh();
      void generated;
    } catch (cause) { setActionError(cause instanceof Error ? cause.message : "Generation failed"); }
    finally { setGenerating(false); }
  };
  const manual = async () => {
    const created = await api.post<Workout>("/api/workouts/manual", { location, durationPresetMinutes: duration, energy: 3, soreness: [], constraints: "" });
    const started = await api.post<Workout>(`/api/workouts/${created.id}/start`);
    navigate(`/workout/${started.id}`);
  };
  const deleteSuggestion = async (selected: Workout) => {
    const date = new Date(selected.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
    if (!window.confirm(`Permanently delete “${selected.title}” (${date})?`)) return;
    try { await api.delete(`/api/workouts/${selected.id}`); await refresh(); }
    catch (cause) { setActionError(cause instanceof Error ? cause.message : "Could not delete workout"); }
  };

  const last = data.recentWorkouts.find((item) => item.status === "completed");
  const weight = data.measurements.find((item) => item.bodyweightKg !== null)?.bodyweightKg;
  return <>
    <PageHeader eyebrow={new Intl.DateTimeFormat("en-AU", { weekday: "long", day: "numeric", month: "long" }).format(new Date())} title="Today" />
    <section className="metric-strip">
      <div><span>Bodyweight</span><strong>{weight ? `${weight} kg` : "—"}</strong></div>
      <div><span>This week</span><strong>{data.recentWorkouts.filter((item) => Date.now() - Date.parse(item.date) < 7 * 86_400_000 && item.status === "completed").length} sessions</strong></div>
      <div><span>Last</span><strong>{last ? formatRelative(last.date) : "—"}</strong></div>
    </section>

    {workout ? <section className="hero-card">
      <div className="card-topline"><span className="source-pill">{workout.source === "dot" ? "Dot" : workout.source === "codex" ? "Codex · Sol" : workout.source === "openai" ? "API · Terra" : "Manual"}</span><span>{workout.estimatedMinutes} min</span></div>
      <h2>{workout.title}</h2>
      <div className="exercise-preview">
        {workout.exercises.slice(0, 5).map((exercise, index) => <div key={exercise.id}><span>{index + 1}</span><strong>{exercise.name}</strong><small>{exercise.sets.length} sets · {exercise.restSeconds}s rest</small></div>)}
      </div>
      {actionError && <div className="form-error">{actionError}</div>}
      <button className="primary-button full" onClick={() => void start(workout)}>{workout.status === "in_progress" ? "Resume workout" : "Start workout"}</button>
      {workout.status === "suggested" && <button className="danger-text-button full" onClick={() => void deleteSuggestion(workout)}>Delete suggestion</button>}
    </section> : <section className="hero-card no-suggestion">
      <div className="eyebrow">No suggestion yet</div><h2>What fits today?</h2>
      <p>Run <code>$suggest-workout</code> in Codex Desktop, or use the API fallback.</p>
      <div className="segmented"><button className={duration === 60 ? "active" : ""} onClick={() => setDuration(60)}>60 min</button><button className={duration === 90 ? "active" : ""} onClick={() => setDuration(90)}>90 min</button></div>
      <div className="segmented"><button className={location === "gym" ? "active" : ""} onClick={() => setLocation("gym")}>Commercial gym</button><button className={location === "home" ? "active" : ""} onClick={() => setLocation("home")}>Home</button></div>
      {actionError && <div className="form-error">{actionError}</div>}
      <button className="primary-button full" disabled={generating} onClick={() => void generate()}>{generating ? "Generating…" : "Generate with Terra"}</button>
      <button className="text-button full" onClick={() => void manual()}>Start a blank workout</button>
    </section>}

    <section className="section-block">
      <div className="section-heading"><div><div className="eyebrow">Active priorities</div><h2>Goals</h2></div></div>
      <div className="goal-row">
        {data.goals.filter((goal) => goal.status === "active").map((goal) => <article key={goal.id} className="mini-card"><span className="priority">P{goal.priority}</span><strong>{goal.title}</strong><small>{goal.targetDate ? `Target ${new Date(`${goal.targetDate}T00:00:00`).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}` : goal.category}</small></article>)}
      </div>
    </section>
  </>;
}

function formatRelative(date: string) {
  const days = Math.floor((Date.now() - Date.parse(date)) / 86_400_000);
  return days === 0 ? "Today" : days === 1 ? "Yesterday" : `${days}d ago`;
}
